import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Badge } from '@components';
import { DeliveryLog } from '../types';

interface DeliveryLogDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  log: DeliveryLog | null;
}

export const DeliveryLogDetailModal: React.FC<DeliveryLogDetailModalProps> = ({
  isOpen,
  onClose,
  log,
}) => {
  const { t } = useTranslation();

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'success';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const getTypeVariant = (type: string) => {
    switch (type) {
      case 'email':
        return 'info';
      case 'sms':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const formatTimestamp = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  if (!log) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('communications.deliveryLogDetails')}
      size="lg"
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
              {t('communications.timestamp')}
            </h3>
            <p className="text-sm text-gray-900 dark:text-white">
              {formatTimestamp(log.created || log.sent_at)}
            </p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
              {t('communications.type')}
            </h3>
            <div className="mt-1">
              <Badge variant={getTypeVariant(log.delivery_type)} size="sm">
                {log.delivery_type.toUpperCase()}
              </Badge>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
              {t('communications.recipient')}
            </h3>
            <p className="text-sm text-gray-900 dark:text-white">{log.recipient}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
              {t('communications.template')}
            </h3>
            <p className="text-sm text-gray-900 dark:text-white">
              {log.template || log.template_name || 'N/A'}
            </p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
              {t('common.status')}
            </h3>
            <div className="mt-1">
              <Badge variant={getStatusVariant(log.status)} size="sm">
                {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
              </Badge>
            </div>
          </div>
          {log.delivered_at && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                {t('communications.deliveredAt')}
              </h3>
              <p className="text-sm text-gray-900 dark:text-white">
                {formatTimestamp(log.delivered_at)}
              </p>
            </div>
          )}
          {log.failed_at && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                {t('communications.failedAt')}
              </h3>
              <p className="text-sm text-gray-900 dark:text-white">
                {formatTimestamp(log.failed_at)}
              </p>
            </div>
          )}
        </div>
        {log.error_message && (
          <div>
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
              {t('communications.errorMessage')}
            </h3>
            <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded">
              {log.error_message}
            </p>
          </div>
        )}
        <div>
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
            {t('communications.logId')}
          </h3>
          <p className="text-sm text-gray-900 dark:text-white font-mono">{log.id}</p>
        </div>
        {log.template_id && (
          <div>
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
              {t('communications.templateId')}
            </h3>
            <p className="text-sm text-gray-900 dark:text-white font-mono">{log.template_id}</p>
          </div>
        )}
      </div>
    </Modal>
  );
};
